import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonMC from '../../components/ButtonMC';
import IconMC from '../../components/IconMC';
import CustomerInfoCard from './CustomerInfoCard';
import useCallPermissionActions from '../../hooks/fonema/actions/useCallPermissionActions';
import useVoiceOutboundFrom from '../../hooks/voice/application/useVoiceOutboundFrom';
import PermissionMessagePreviewMC from './PermissionMessagePreviewMC';
import WhatsappCallHeaderMC from './WhatsappCallHeaderMC';
import CallLineSelector from '../../modules/calls/CallLineSelector';

/**
 * RequestPermissionMC Component
 *
 * Displays a screen to request permission from a customer before making a WhatsApp call.
 * Multi-number: requests are scoped to the selected outbound line.
 *
 * @param {Object} props - Component props
 * @param {Object} props.client - The selected client object containing customer information
 * @param {Function} props.onClose - Function to close the sidebar or go back
 */
const RequestPermissionMC = ({ client, onClose }) => {
  const { t } = useTranslation('Inbox');
  const {
    requestPermission,
    requestingPermission,
    isVoiceProvider
  } = useCallPermissionActions();
  const {
    enabledLines,
    selectedFrom,
    selectedAppId,
    selectedPlatformRetailerId,
    setSelectedFrom,
    requiresFromSelection,
    mustConfirmFrom
  } = useVoiceOutboundFrom({ enabled: isVoiceProvider });

  const canRequestPermission = useMemo(
    () => client?.callingPermission?.canRequestPermission === true,
    [client]
  );
  const showCallLineSelector = isVoiceProvider && enabledLines.length > 1;
  const lineScope = useMemo(() => ({
    appId: selectedAppId || undefined,
    platformRetailerId: selectedPlatformRetailerId || undefined
  }), [selectedAppId, selectedPlatformRetailerId]);

  const handleRequestPermission = useCallback(async () => {
    await requestPermission(client, {
      onSuccess: onClose,
      lineScope
    });
  }, [client, lineScope, onClose, requestPermission]);

  if (!client) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-64 tw-text-gray-500">
        {t('wsCallSidebar.noClientSelected')}
      </div>
    );
  }

  const requestDisabled = requestingPermission
    || (isVoiceProvider && (requiresFromSelection || (mustConfirmFrom && !selectedAppId)));

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-start tw-p-4 tw-shadow-[0px_1px_4px_0px_#D1D1D1] tw-rounded-lg">
      <WhatsappCallHeaderMC />

      <CustomerInfoCard client={client} />

      {showCallLineSelector && (
        <div className="tw-w-full tw-mt-3">
          <CallLineSelector
            enabledLines={enabledLines}
            selectedFrom={selectedFrom}
            onChange={setSelectedFrom}
            disabled={requestingPermission}
            variant="light"
          />
        </div>
      )}

      <div className="tw-w-full tw-mt-4 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-flex tw-gap-2">
        <IconMC name="info-outline" size="base" className="tw-text-blue-500 tw-flex-shrink-0 tw-mt-0.5" />
        <p className="tw-text-xs tw-text-blue-500">
          {t('wsCallSidebar.permissionInfo')}
        </p>
      </div>

      <PermissionMessagePreviewMC />

      <p className="tw-w-full tw-mt-2 tw-text-xs tw-text-gray-600">
        {t('wsCallSidebar.messagePreview')}
      </p>

      {canRequestPermission ? (
        <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-mt-5">
          <ButtonMC
            type="button"
            onClick={handleRequestPermission}
            text={t('wsCallSidebar.requestPermission')}
            variant="PRIMARY"
            size="medium"
            className="tw-w-full"
            disabled={requestDisabled}
          />
        </div>
      ) : (
        <p className="tw-w-full tw-mt-5 tw-text-xs tw-text-gray-600">
          {t(
            'wsCallSidebar.permissionRequestNotAllowed',
            { defaultValue: 'Este contacto no permite solicitar permisos de llamada en este momento.' }
          )}
        </p>
      )}
    </div>
  );
};

export default RequestPermissionMC;
